import { c as _c } from "react/compiler-runtime";
import { MembersEmptyPage } from "@components/common/empty-page/members-empty-page";
import { getWorkspaceMembersTableColumns, SEATS_COLUMN } from "@components/table/members/members-table-constants";
import { EntitiesToolbar } from "@components/common/toolbar/entities-toolbar";
import { MembersTable } from "@components/table/members/members-table";
import { MemberHeaders } from "@components/table/members/members-table-utils";
import { MembersSort } from "@components/table/members/members-sort";
import { ColumnPreferencesProvider } from "@context-providers/table/column-preferences-provider";
import { useCompanyMembers } from "@hooks/use-company-members";
import { useMediaQueryList } from "@hooks/use-media-query";
import { useSubjectsSortByQuery } from "@hooks/use-subjects-sort-by-query";
import { InviteMemberToCompany } from "@pages/members/invite-member-to-company";
import { MainMemberDetails } from "@pages/members/main-member-details";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useCompanyMemberBulkActions } from "@src/pages/members/use-company-member-bulk-actions";
import { isMembersSortBy } from "@custom-types/type-guards";
import { fetchedMembersFlagsSelector, fetchingMembersFlagsSelector, filteredCompanyMembersSelector } from "@store/members/members-selector";
import { fetchingSdbCompanyFlagsSelector, selectedSdbCompanySelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { searchSelector } from "@store/ui/ui-selector";
import { currentUserSelector } from "@store/user/user-selector";
import { getExportComponent, sortMembers } from "@utils/member-utils";
import { useEffect } from "react";
import { MembersMobileView } from "@pages/members/members-mobile-view";
import { resetTableState } from "@store/table/table-slice";
import { DEFAULT_WORKSPACE_MEMBERS_SORT_BY } from "@components/table/members/members-sort-constants";
import { useMemberSeatsLookup } from "@hooks/use-member-seats-lookup";
import { doesPlanIncludeSeats } from "@utils/member-seats-utils";
import { useFetchCompanySubscriptions } from "@hooks/subscription/use-fetch-company-subscriptions";
import { useFetchActiveMembers } from "@hooks/use-fetch-active-members";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { areActiveMembersLoadingSelector } from "@store/active-members/active-members-selector";
import { useMembersFilters } from "@components/table/members/members-filters/use-members-filters";
import { useDeselectHiddenMembers } from "@pages/group-details/use-deselect-hidden-members";

/**
 * The columns that are shown by default. The status and the seats are hidden until the user shows
 * them or filters by them, see FILTERED_COLUMN_FIELDS.
 */
const DEFAULT_VISIBLE_COLUMNS = ["user", "email", "role"];

/** The hidden by default columns that are shown for as long as the members are filtered by them */
const FILTERED_COLUMN_FIELDS = {
  [MemberHeaders.status]: "status",
  [MemberHeaders.seats]: SEATS_COLUMN
};

/** Listing all the members of a company in a table */
export function MembersPage() {
  const $ = _c(41);
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    isScreenSmOrSmaller
  } = useMediaQueryList();
  useFetchCompanySubscriptions();
  useCompanyMembers();
  const seatsLookup = useMemberSeatsLookup();
  const shouldShowSeats = doesPlanIncludeSeats(seatsLookup);
  const {
    canInviteUsersToCompany,
    canViewSubscriptionDetails
  } = useHasUserValidRoleCompanyLevel();
  const shouldFetchActiveMembers = seatsLookup.isUserRolePlan && canViewSubscriptionDetails;
  useFetchActiveMembers(shouldFetchActiveMembers);
  const areActiveMembersLoading = useAppSelector(areActiveMembersLoadingSelector);
  const currentUser = useAppSelector(currentUserSelector);
  const filteredCompanyMembers = useAppSelector(filteredCompanyMembersSelector);
  const {
    isFetchingCompanyMembers
  } = useAppSelector(fetchingMembersFlagsSelector);
  const {
    isFetchingCompanySubscriptions
  } = useAppSelector(fetchingSdbCompanyFlagsSelector);
  const {
    hasFetchedWorkspaceMembers
  } = useAppSelector(fetchedMembersFlagsSelector);
  const selectedSdbCompany = useAppSelector(selectedSdbCompanySelector);
  const {
    debouncedSearchText
  } = useAppSelector(searchSelector);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      defaultSort: DEFAULT_WORKSPACE_MEMBERS_SORT_BY,
      isValidSort: isMembersSortBy
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    sortBy,
    setSortBy
  } = useSubjectsSortByQuery(t0);
  const sortedMembers = sortMembers(filteredCompanyMembers, sortBy, seatsLookup);
  const {
    filteredMembers,
    filterChips,
    filteredColumns
  } = useMembersFilters({
    members: sortedMembers,
    tableSubject: "workspace"
  });
  useDeselectHiddenMembers(filteredMembers);
  const isLoading = isFetchingCompanyMembers || isFetchingCompanySubscriptions || shouldFetchActiveMembers && areActiveMembersLoading;
  const isAloneInWorkspace = hasFetchedWorkspaceMembers && !debouncedSearchText.length && sortedMembers.every(member => member.identity === currentUser?.identity);
  const hasNoSearchResults = !isLoading && hasFetchedWorkspaceMembers && !!debouncedSearchText.length && sortedMembers.length === 0;
  const shouldShowEmptyPage = isAloneInWorkspace || hasNoSearchResults;
  let t1;
  if ($[1] !== filteredMembers || $[2] !== isAloneInWorkspace) {
    t1 = isAloneInWorkspace ? [] : filteredMembers;
    $[1] = filteredMembers;
    $[2] = isAloneInWorkspace;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const listedMembers = t1;
  let t2;
  if ($[4] !== filteredMembers || $[5] !== selectedSdbCompany) {
    t2 = {
      members: filteredMembers,
      company: selectedSdbCompany
    };
    $[4] = filteredMembers;
    $[5] = selectedSdbCompany;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const buttonComponents = useCompanyMemberBulkActions(t2);
  let t3;
  let t4;
  if ($[7] !== dispatch) {
    t3 = () => () => {
      dispatch(resetTableState());
    };
    t4 = [dispatch];
    $[7] = dispatch;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  let t5;
  if ($[10] !== hasFetchedWorkspaceMembers || $[11] !== isAloneInWorkspace) {
    t5 = hasFetchedWorkspaceMembers && !isAloneInWorkspace && <MainMemberDetails />;
    $[10] = hasFetchedWorkspaceMembers;
    $[11] = isAloneInWorkspace;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = listedMembers.length;
  let t7;
  if ($[13] !== canInviteUsersToCompany || $[14] !== shouldShowEmptyPage) {
    t7 = !shouldShowEmptyPage && canInviteUsersToCompany && <InviteMemberToCompany buttonVariant="filled" />;
    $[13] = canInviteUsersToCompany;
    $[14] = shouldShowEmptyPage;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = listedMembers.length > 0 ? filterChips : undefined;
  const t9 = listedMembers.length > 0 && <MembersSort sortBy={sortBy} setSortBy={setSortBy} tableSubject="workspace" hiddenFields={shouldShowSeats ? undefined : [SEATS_COLUMN]} />;
  let t10;
  if ($[16] !== buttonComponents.export || $[17] !== listedMembers.length) {
    t10 = getExportComponent(listedMembers.length, buttonComponents.export);
    $[16] = buttonComponents.export;
    $[17] = listedMembers.length;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== isLoading || $[20] !== listedMembers.length || $[21] !== t10 || $[22] !== t7 || $[23] !== t8 || $[24] !== t9) {
    t11 = <EntitiesToolbar toolbarEntityName="Member" numberOfRows={t6} isLoading={isLoading} mobileAction={t7} filterChips={t8} sortActions={t9} exportComponent={t10} />;
    $[19] = isLoading;
    $[20] = listedMembers.length;
    $[21] = t10;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== shouldShowEmptyPage) {
    t12 = shouldShowEmptyPage && <MembersEmptyPage subtitle="All of your workspace members will be found here. You can add members to your workspace by inviting them." button={<InviteMemberToCompany buttonVariant="filled" />} />;
    $[26] = shouldShowEmptyPage;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== buttonComponents || $[29] !== filteredMembers || $[30] !== isLoading || $[31] !== isScreenSmOrSmaller || $[32] !== shouldShowEmptyPage || $[33] !== sortBy) {
    t13 = isScreenSmOrSmaller && !shouldShowEmptyPage && <MembersMobileView bulkActionButtons={buttonComponents} members={filteredMembers} isLoading={isLoading} tableSubject="workspace" sortBy={sortBy} />;
    $[28] = buttonComponents;
    $[29] = filteredMembers;
    $[30] = isLoading;
    $[31] = isScreenSmOrSmaller;
    $[32] = shouldShowEmptyPage;
    $[33] = sortBy;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const t14 = !isScreenSmOrSmaller && !shouldShowEmptyPage && <ColumnPreferencesProvider tableKey="workspace-members-table" columns={getWorkspaceMembersTableColumns(shouldShowSeats)} options={{
    nonConfigurableColumns: ["options"],
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
    temporarilyVisibleColumns: filteredColumns.flatMap(_temp)
  }}><MembersTable companyId={companyId} members={filteredMembers} tableSubject="workspace" isLoading={isLoading} bulkActionButtons={buttonComponents} sortBy={sortBy} setSortBy={setSortBy} /></ColumnPreferencesProvider>;
  let t15;
  if ($[35] !== t11 || $[36] !== t12 || $[37] !== t13 || $[38] !== t14 || $[39] !== t5) {
    t15 = <>{t5}{t11}{t12}{t13}{t14}</>;
    $[35] = t11;
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
    $[39] = t5;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  return t15;
}
function _temp(column) {
  return FILTERED_COLUMN_FIELDS[column] ?? [];
}